import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["selectAll", "checkbox", "toolbar", "selectedCount", "form", "normalToolbar", "item", "confirmDialog", "confirmCount", "confirmList", "judgmentForm"]
  static values = { selecting: { type: Boolean, default: false }, reorderUrl: String }

  toggleSelectMode() {
    this.selectingValue = !this.selectingValue
    this.render()
  }

  toggle() {
    const checked = this.selectAllTarget.checked
    this.checkboxTargets.forEach((cb) => { cb.checked = checked })
    this.updateCount()
  }

  updateCount() {
    const count = this.checkboxTargets.filter((cb) => cb.checked).length
    this.selectedCountTarget.textContent = `${count}件選択中`

    const allChecked = this.checkboxTargets.length > 0 && this.checkboxTargets.every((cb) => cb.checked)
    this.selectAllTarget.checked = allChecked
  }

  deselectAll() {
    this.checkboxTargets.forEach((cb) => { cb.checked = false })
    this.selectAllTarget.checked = false
    this.updateCount()
  }

  // 削除確認ダイアログを開く
  confirmDelete(event) {
    event.preventDefault()
    const selectedCheckboxes = this.checkboxTargets.filter((cb) => cb.checked)
    if (selectedCheckboxes.length === 0) return

    // 削除対象の設問情報を収集
    this.confirmCountTarget.textContent = selectedCheckboxes.length
    this.confirmListTarget.innerHTML = ""
    selectedCheckboxes.forEach((cb) => {
      // NOTE: 親設問は data-bulk-delete-target='item'、マトリクス子設問は data-sortable-target='item'
      const item = cb.closest("[data-bulk-delete-target='item'], [data-sortable-target='item']")
      const qNum = item?.querySelector(".text-indigo-600, .text-indigo-500")?.textContent?.trim() || ""
      const body = item?.querySelector("p.whitespace-pre-wrap, p.text-sm")?.textContent?.trim() || ""
      const li = document.createElement("li")
      li.className = "flex items-start gap-2 text-sm text-gray-700"
      li.innerHTML = `<span class="font-bold text-indigo-600 shrink-0">${this.escapeHtml(qNum)}</span><span class="text-gray-600 line-clamp-2">${this.escapeHtml(body)}</span>`
      this.confirmListTarget.appendChild(li)
    })

    this.confirmDialogTarget.showModal()
  }

  cancelDelete() {
    this.confirmDialogTarget.close()
  }

  // 選択中の設問へ判定条件を一括適用する
  applyJudgmentCondition(event) {
    event.preventDefault()
    const selectedIds = this.checkboxTargets.filter((cb) => cb.checked).map((cb) => cb.value)
    if (selectedIds.length === 0) return

    const form = this.judgmentFormTarget
    form.querySelectorAll("input[name='question_ids[]']").forEach((el) => el.remove())
    selectedIds.forEach((id) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "question_ids[]"
      input.value = id
      form.appendChild(input)
    })

    form.requestSubmit()
  }

  executeDelete() {
    const selectedIds = this.checkboxTargets.filter((cb) => cb.checked).map((cb) => cb.value)
    if (selectedIds.length === 0) return

    const form = this.formTarget
    form.querySelectorAll("input[name='question_ids[]']").forEach((el) => el.remove())
    selectedIds.forEach((id) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "question_ids[]"
      input.value = id
      form.appendChild(input)
    })

    this.confirmDialogTarget.close()
    form.requestSubmit()
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  render() {
    const selecting = this.selectingValue

    // ツールバーの表示切替
    this.toolbarTarget.classList.toggle("hidden", !selecting)
    this.normalToolbarTarget.classList.toggle("hidden", selecting)

    // チェックボックスの表示切替
    this.checkboxTargets.forEach((cb) => {
      cb.closest("[data-checkbox-wrapper]").classList.toggle("hidden", !selecting)
    })

    // ドラッグハンドルの表示切替
    this.itemTargets.forEach((item) => {
      const handle = item.querySelector("[data-drag-handle]")
      if (handle) handle.classList.toggle("hidden", selecting)
    })

    // 三点メニューの表示切替
    this.itemTargets.forEach((item) => {
      const menu = item.querySelector("[data-item-menu]")
      if (menu) menu.classList.toggle("hidden", selecting)
    })

    // マトリクス子設問（sortable item）のドラッグハンドル・三点メニューも選択モード中は隠す
    this.element.querySelectorAll("[data-sortable-target='item']").forEach((child) => {
      const handle = child.querySelector("[data-sortable-target='handle']")
      if (handle) handle.classList.toggle("hidden", selecting)
      const menu = child.querySelector("[data-controller~='dropdown']")
      if (menu) menu.classList.toggle("hidden", selecting)
    })

    // 選択モード解除時にチェックをリセット
    if (!selecting) {
      this.deselectAll()
    }
  }

  // D&D 並べ替え
  dragStart(event) {
    if (this.selectingValue) return
    this.draggedItem = event.currentTarget.closest("[data-bulk-delete-target='item']")
    this.draggedItem.classList.add("opacity-50")
    event.dataTransfer.effectAllowed = "move"
  }

  dragOver(event) {
    if (this.selectingValue || !this.draggedItem) return
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    const item = event.currentTarget
    if (item === this.draggedItem) return

    const rect = item.getBoundingClientRect()
    const midY = rect.top + rect.height / 2
    const container = item.parentNode

    if (event.clientY < midY) {
      container.insertBefore(this.draggedItem, item)
    } else {
      container.insertBefore(this.draggedItem, item.nextSibling)
    }
  }

  drop(event) {
    event.preventDefault()
  }

  dragEnd() {
    if (!this.draggedItem) return
    this.draggedItem.classList.remove("opacity-50")
    this.draggedItem = null
    this.saveOrder()
  }

  async saveOrder() {
    const items = this.itemTargets
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content

    const promises = items.map((item, index) => {
      const questionId = item.dataset.questionId
      const url = this.reorderUrlValue.replace("__ID__", questionId)
      return fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({ position: index })
      })
    })

    await Promise.all(promises)
  }
}
